<template>
  <div class="auth-callback">
    <div v-if="isLoading" class="loading-container">
      <div class="spinner"></div>
      <p>Connecting your Discogs account...</p>
    </div>

    <div v-else-if="error" class="error-container">
      <h3>Authentication Error</h3>
      <p>{{ error }}</p>
      <button @click="retryAuth">Try Again</button>
    </div>

  </div>
</template>

<script>
import { defineComponent, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { handleCallback } from '@/services/auth';
import { takePostLoginRedirect } from '@/services/postLoginRedirect';
import { clearCollection } from '@/composables/useCollection';

export default defineComponent({
  name: 'DiscogsCallback',

  setup() {
    const router = useRouter();
    const isLoading = ref(true);
    const error = ref(null);

    onMounted(async () => {
      try {
        console.log('DiscogsCallback: Handling OAuth callback');

        // Extract OAuth parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const oauthToken = urlParams.get('oauth_token');
        const oauthVerifier = urlParams.get('oauth_verifier');

        if (!oauthToken || !oauthVerifier) {
          throw new Error('Missing OAuth parameters. Authentication failed.');
        }

        console.log('DiscogsCallback: OAuth parameters received');

        // Handle the Discogs OAuth callback to get access tokens
        const discogsData = await handleCallback({
          oauth_token: oauthToken,
          oauth_verifier: oauthVerifier,
        });

        if (!discogsData || !discogsData.accessToken) {
          throw new Error('Failed to get access tokens from Discogs.');
        }

        console.log('DiscogsCallback: Successfully obtained Discogs tokens');

        // Store Discogs tokens in localStorage for now (instead of Firebase)
        localStorage.setItem('discogs_auth', JSON.stringify({
          accessToken: discogsData.accessToken,
          accessTokenSecret: discogsData.accessTokenSecret,
          timestamp: Date.now(),
        }));

        // Drop any collection cached for a previous session/account
        clearCollection();
        console.log('DiscogsCallback: Tokens saved to localStorage');
        // Straight back to where the user was headed; the spinner stays up until then
        router.replace(takePostLoginRedirect());
      } catch (err) {
        console.error('Callback error:', err);
        error.value = err.message || 'An error occurred during authentication.';
        isLoading.value = false;
      }
    });

    // Retry authentication
    const retryAuth = () => {
      const callbackUrl = `${window.location.origin}/callback`;
      window.location.href = `/login?callback=${encodeURIComponent(callbackUrl)}`;
    };

    return {
      isLoading,
      error,
      retryAuth,
    };
  },
});
</script>

<style scoped>
.auth-callback {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  padding: 2rem;
  text-align: center;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.spinner {
  border: 4px solid rgba(0, 0, 0, 0.1);
  border-radius: 50%;
  border-top: 4px solid #3498db;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.error-container {
  max-width: 500px;
  padding: 1.5rem;
  border-radius: 8px;
  background-color: #fff0f0;
  border: 1px solid #ffcaca;
}

.error-container h3 {
  color: #e74c3c;
  margin-top: 0;
}

.error-container button {
  background-color: #3498db;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: bold;
  margin-top: 1rem;
}

.error-container button:hover {
  background-color: #2980b9;
}
</style>
